/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */
import {Navigate, Route, Routes} from 'react-router-dom';
import {PrivateRoutes} from './PrivateRoutes';
import {ErrorsPage} from '../modules/errors/ErrorsPage';
import {QueryClient, QueryClientProvider} from 'react-query';
import {ReactQueryDevtools} from 'react-query/devtools';
import {MetronicI18nProvider} from '../../_metronic/i18n/Metronici18n';
import {PermissionProvider} from '../modules/auth/authorization/PermissionProvider';
import {Toaster} from 'react-hot-toast';
import EcDeskRedirect from './EcDeskRedirect';
import {useNativeAuth} from '../modules/auth/native/NativeAuthProvider';
import NativeLogin from '../modules/auth/native/NativeLogin';

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const toaster = (
  <Toaster
    toastOptions={{
      duration: 5000,
      success: {style: {border: '1px solid green'}},
      error: {style: {border: '1px solid red'}},
    }}
  />
);

// Routes available once authenticated.
const authenticatedRoutes = (
  <>
    <Route path="/ec-desk" element={<EcDeskRedirect />} />
    <Route path="/*" element={<PrivateRoutes />} />
    <Route index element={<Navigate to="/dashboard" />} />
  </>
);

// Entra native-auth flow: custom login -> real home page.
const AppRoutes = (): JSX.Element => {
  const {isAuthenticated, login} = useNativeAuth();

  if (!isAuthenticated) {
    return <NativeLogin onCompleted={login} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <MetronicI18nProvider>
        <PermissionProvider>
          <>
            {toaster}
            <Routes>
              <Route path="error/*" element={<ErrorsPage />} />
              <Route path="logout" element={<Navigate to="/logout" />} />
              {authenticatedRoutes}
            </Routes>
          </>
        </PermissionProvider>
      </MetronicI18nProvider>
    </QueryClientProvider>
  );
};

export {AppRoutes};
